
import { DateTime, IANAZone } from "luxon";
import { getLogConsole } from "../services/log.service";
import { floorKey } from "./collection.tools";
import { TIMESTAMP_DATE_ONLY_FORMAT, TIMESTAMP_FORMAT } from "./timestamps.tools.definitions";

//#region Override IANAZone to improve performance

/** Keep original IANAZone functions */
let _originalIANAZoneFunctions: {
    /** original create zone function */
    create(name: string): IANAZone,
    /** original resetCache function */
    resetCache(): void
} | undefined = undefined;
/** Cache of zones by name */
let _ianaZoneCache: Record<string, IANAZone> = {};

/**
 * Override IANAZone functions to use OptimizedIANAZone instead of IANAZone
 */
export function installOptimizedIANAZone(): void {

    if (_originalIANAZoneFunctions == null) {
        _originalIANAZoneFunctions = {
            create: IANAZone.create,
            resetCache: IANAZone.resetCache
        };

        IANAZone.create = function create(name) {
            if (!_ianaZoneCache[name]) {
                // install standard zone, it will be used to initialize the optimized zone cache
                _ianaZoneCache[name] = new IANAZone(name);

                // install optimized zone (it uses the standard zone in initOffsetCache)
                const optimizedZone = new OptimizedIANAZone(name);
                if (optimizedZone.initDSTcache()) {
                    // we register the OptimizedIANAZone if init cache was successful
                    _ianaZoneCache[name] = optimizedZone;
                }
            }
            return _ianaZoneCache[name];
        };

        IANAZone.resetCache = function resetCache() {
            _ianaZoneCache = {};
            _originalIANAZoneFunctions?.resetCache();
        };
    }
}
//#endregion Override IANAZone to improve performance

type DSTcache = {
    /** milliseconds timestamps sorted keys of the map */
    keys: number[],
    /** milliseconds timestamp => offset after this timestamp (null => unknown)*/
    offsetMap: Record<number, { dt: DateTime, offset: number | null }>
}

/**
 * Compute all Daylight saving DateTimes between dates
 */
function computeDSTCache(fromDate: DateTime, toDate: DateTime): DSTcache {
    const result: DSTcache = { keys: [], offsetMap: {} };

    const units = [{ month: 4 }, { month: 1 }, { week: 2 }, { week: 1 }, { day: 3 }, { day: 1 }, { hour: 12 }, { hour: 4 }, { hour: 2 }, { hour: 1 }];
    // const unitsUsage = units.map(u => 0); // BGS please keep line commented this to ease performance optimizations
    const maxUnit = units.length - 1;
    let currentUnitIndex = 0;// start iteration with biggest unit

    let currentDate = fromDate.startOf("hour");// start of hour to eliminate minutes seconds and milliseconds
    let currentOffset = currentDate.offset;

    // record offset
    let mTs = currentDate.toMillis();
    result.keys.push(mTs);
    result.offsetMap[mTs] = { dt: currentDate, offset: currentOffset };

    while (currentDate < toDate) {
        // unitsUsage[currentUnitIndex]++; // BGS please keep line commented this to ease performance optimizations
        const date = currentDate.plus(units[currentUnitIndex]);
        const offset = date.offset;
        if (offset !== currentOffset) {
            // offset changed
            if (currentUnitIndex < maxUnit) {
                // we have gone too far, try with a lower unit
                currentUnitIndex++;
            } else {
                // DST change detected

                // record offset
                const mTs = date.toMillis();
                result.keys.push(mTs);
                result.offsetMap[mTs] = { dt: date, offset: offset };

                // continue iteration from date with the biggest unit
                currentOffset = offset;
                currentDate = date;
                currentUnitIndex = 0;// use biggest unit
            }
        } else {
            // offset did not change => continue with this unit
            currentDate = date;
        }
    }

    // record 'null' after toDate
    mTs = toDate.toMillis();
    result.keys.push(mTs);
    result.offsetMap[mTs] = { dt: toDate, offset: null };// we don't know the offset after toDate

    // console.log(units.map((unit, index) => JSON.stringify(unit) + " => " + unitsUsage[index]).join("\n")); // BGS please keep line commented this to ease performance optimizations

    return result;
}

/**
 * This function is used by unit tests
 * @param from date string "2021-01-01"
 * @param to date string "2025-01-01"
 * @param timeZone "Europe/Paris"
 */
export function computeDSTCacheForTests(from: string, to: string, timeZone: string): { date: string, offset: number | null }[] {
    const result = computeDSTCache(DateTime.fromFormat(from, TIMESTAMP_DATE_ONLY_FORMAT, { zone: timeZone }), DateTime.fromFormat(to, TIMESTAMP_DATE_ONLY_FORMAT, { zone: timeZone }));
    return result.keys.map(ts => {
        return {
            date: DateTime.fromMillis(ts).setZone(timeZone).toFormat(TIMESTAMP_FORMAT),
            offset: result.offsetMap[ts].offset
        };
    });
}

// DST are precomputed for CACHE_YEAR_COUNT around today (ie for CACHE_YEAR_COUNT * 2 years)
const CACHE_YEAR_COUNT = 5;// TODO make cache dynamic to adapt to any period
class OptimizedIANAZone extends IANAZone {

    _dstCache?: DSTcache;

    /**
     * initialize the zone offset cache
     * @returns true if cache init was successful
     */
    public initDSTcache(): boolean {
        try {
            const currentDate = DateTime.now().setZone(this.name);
            // compute DST cache
            this._dstCache = computeDSTCache(
                currentDate.minus({ year: CACHE_YEAR_COUNT }),
                currentDate.plus({ year: CACHE_YEAR_COUNT }));
            return true;
        } catch (ex) {
            getLogConsole().error(ex);
            return false;
        }

    }

    /**
     * Return the offset in minutes for this zone at the specified timestamp.
     *
     * @param ts - Epoch milliseconds for which to compute the offset
     */
    public override offset(ts: number): number {
        if (this._dstCache) {
            const dstChangeTs = floorKey(this._dstCache.keys, ts);// get previous DST change timestamp
            if (dstChangeTs != null) {
                // get offset that is active after dstChangeTs
                const result = this._dstCache.offsetMap[dstChangeTs].offset;
                if (result != null) {
                    return result;
                }// else ts is after last value in the cache
            }// else ts is before first value in the cache
        }
        // outside cache bounds => use standard computation
        const result = super.offset(ts);
        return result;
    }
}